/**
 * Tutorías (M3) — lógica PURA del ciclo entrega→feedback→reentrega (espejo TS de
 * `core_rust::use_cases::tutoring`). El write-path del servidor es la autoridad;
 * esto gatea la UI (botón «reentregar», cálculo de SLA/vencido) sin red.
 */

/** Política de revisión efectiva (resuelta desde defaults de org + override). */
export interface ReviewPolicy {
  allowResubmission: boolean;
  maxVersions: number;
  slaDays: number;
}

/** Fallback cuando la org aún no configuró tutorías (== ReviewPolicy::FALLBACK). */
export const FALLBACK_POLICY: ReviewPolicy = {
  allowResubmission: true,
  maxVersions: 3,
  slaDays: 3,
};

export interface OrgDefaults {
  allowResubmission: boolean;
  maxVersions: number;
  slaDays: number;
}

export interface AssignmentOverride {
  allowResubmission?: boolean | null;
  maxVersions?: number | null;
  slaDays?: number | null;
}

function firstDefined<T>(over: T | null | undefined, base: T): T {
  return over === null || over === undefined ? base : over;
}

/**
 * Resuelve la política efectiva. Precedencia: override de tarea → default de org
 * → fallback. La corrección ÚNICA del proyecto final fuerza `maxVersions=1` y
 * `allowResubmission=false` (gana sobre todo).
 */
export function resolvePolicy(
  org: OrgDefaults | null | undefined,
  over: AssignmentOverride = {},
  finalProjectSingleCorrection = false,
): ReviewPolicy {
  const base: OrgDefaults = org ?? { ...FALLBACK_POLICY };
  const p: ReviewPolicy = {
    allowResubmission: firstDefined(over.allowResubmission, base.allowResubmission),
    maxVersions: Math.max(1, firstDefined(over.maxVersions, base.maxVersions)),
    slaDays: Math.max(0, firstDefined(over.slaDays, base.slaDays)),
  };
  if (finalProjectSingleCorrection) {
    p.allowResubmission = false;
    p.maxVersions = 1;
  }
  return p;
}

/** Nº de la NUEVA versión (la primera es 1). */
export function nextVersion(currentMax: number | null | undefined): number {
  return (currentMax ?? 0) + 1;
}

export type ResubmitDenied = "not_allowed" | "previous_not_returned" | "max_versions_reached";

/**
 * ¿Puede el alumno crear una NUEVA versión? `latest` = última versión existente
 * (`null` = aún ninguna → primera entrega, siempre permitida).
 */
export function canResubmit(
  policy: ReviewPolicy,
  latest: { version: number; outcome: string | null } | null,
): { ok: true } | { ok: false; reason: ResubmitDenied } {
  if (!latest) {
    return { ok: true };
  }
  if (!policy.allowResubmission) {
    return { ok: false, reason: "not_allowed" };
  }
  if (latest.outcome !== "returned") {
    return { ok: false, reason: "previous_not_returned" };
  }
  if (nextVersion(latest.version) > policy.maxVersions) {
    return { ok: false, reason: "max_versions_reached" };
  }
  return { ok: true };
}

/** Fecha límite de corrección (SLA) de una versión pendiente. */
export function slaDueAt(submittedAtISO: string, slaDays: number): Date {
  const d = new Date(submittedAtISO);
  d.setUTCDate(d.getUTCDate() + Math.max(0, slaDays));
  return d;
}

/** ¿La corrección de una versión pendiente está VENCIDA respecto al SLA? */
export function isOverdue(
  submittedAtISO: string,
  slaDays: number,
  now: Date = new Date(),
): boolean {
  return now.getTime() > slaDueAt(submittedAtISO, slaDays).getTime();
}

/** Roles de cohorte que pueden REVISAR (corregir/devolver/aprobar) entregas. */
export function roleCanReview(role: string): boolean {
  return role === "lead" || role === "assistant" || role === "tutor";
}
